/**
 * Base chatbot functionality that can be placed inside a modal window, or as an inline chat on the video page.
 *
 * See chatbot_video_assistant.scss
 */
import * as Sentry from '@sentry/browser';
import lottie from 'lottie-web';
import { parse } from 'marked';

import { buildURL, fetchRequest } from 'Utils/apiFetchRequest';
import { secondsToTimestamp } from 'Utils/text';

const Copy = {
    placeholder: {
        default: 'Ask questions about this video...',
        loading: 'Searching...',
    },
    error: {
        questionResponse: 'Sorry, I could not compute.',
    },
};

const _SUGGESTED_QUESTIONS = [
    {
        id: 11,
        question: 'Summarize this video',
        type: 'predefined',
    },
    {
        id: 12,
        question: 'What trials are discussed in this video?',
        type: 'predefined',
    },
    {
        id: 13,
        question: 'What therapies are mentioned in this video?',
        type: 'predefined',
    },
    {
        id: 14,
        question: 'What are the key practice updates for this disease?',
        type: 'predefined',
    },
];

const _CSS_COLLAPSE_DURATION = 250;

export default class VuMediChatbotInline {
    constructor(videoId, brightcovePlayer = null) {
        this.videoId = videoId;
        this.brightcovePlayer = brightcovePlayer;

        this.suggestionsElement = null;
        this.chatbotElement = null;
        this.uuid = null;
        this.inProgress = false;
        this.asked = [];
    }

    _markQuestionAsAsked(question) {
        this.asked.push(question);
    }

    _removeAskedQuestions(questions) {
        return questions.filter((el) => this.asked.indexOf(el.question) === -1);
    }

    _handleStreamingAnswerResponse(answerElement, question, type = '') {
        return new Promise((resolve) => {
            if (!this.uuid) {
                resolve();
                return;
            }

            const eventSource = new EventSource(
                `/api/chats/${this.uuid}/ask/?question=${encodeURIComponent(question)}&question_source=${encodeURIComponent(type)}`,
            );

            let processedFirstResponse = false;

            eventSource.addEventListener('message', (ev) => {
                try {
                    if (!processedFirstResponse) {
                        this._toggleSkeleton(true, false);
                        processedFirstResponse = true;
                        this._toggleSkeleton(false);
                    }

                    const answerData = JSON.parse(ev.data);
                    this._answerRenderContent(answerElement, answerData.answer, []);

                    if (answerData['last_response']) {
                        let nextBestQuestions = _SUGGESTED_QUESTIONS;

                        if (answerData && Array.isArray(answerData['next_best_questions'])) {
                            nextBestQuestions = nextBestQuestions.concat(answerData['next_best_questions']);
                        }

                        this._answerRenderContent(
                            answerElement,
                            answerData.answer,
                            this._removeAskedQuestions(nextBestQuestions),
                        );
                        eventSource.close();
                        resolve();
                    }
                } catch (error) {
                    eventSource.close();
                    this._answerRenderContent(answerElement, Copy.error.questionResponse);
                    Sentry.captureException(error);
                    resolve();
                }
            });

            eventSource.addEventListener('error', (error) => {
                eventSource.close();
                this._answerRenderContent(answerElement, Copy.error.questionResponse);
                Sentry.captureException(error);
                resolve();
            });
        });
    }

    async _askQuestion(question, type = '') {
        if (!this.chatbotElement) {
            return;
        }

        this.inProgress = true;

        this.chatbotElement.querySelector('.vm-chatbot-body').classList.add('vm-chatbot-body--conversation');
        this.chatbotElement.querySelector('input[type="text"]').value = '';
        this.chatbotElement.querySelector('input[type="text"]').setAttribute('placeholder', Copy.placeholder.loading);
        this.chatbotElement.querySelector('button[type="submit"]').setAttribute('disabled', 'disabled');

        this._markQuestionAsAsked(question);
        this._renderQuestion(question);
        this._toggleSkeleton(true);

        const answerElement = this._answerPrepareElement();

        if (!this.uuid) {
            await this._initializeSession();
        }

        await this._handleStreamingAnswerResponse(answerElement, question, type);

        answerElement.classList.remove('vm-chatbot-message--answer-incomplete');
        this.chatbotElement.querySelector('input[type="text"]').setAttribute('placeholder', Copy.placeholder.default);
        this.chatbotElement.querySelector('button[type="submit"]').removeAttribute('disabled');

        this.inProgress = false;
        this._toggleSkeleton(false);
    }

    _initializeChatbot() {
        return new Promise((resolve, reject) => {
            const params = {
                content_type: 20,
                object_id: this.videoId,
            };

            const requestURL = buildURL('/api/chats/chat-configuration/', params);

            fetchRequest(requestURL)
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    reject();
                    Sentry.captureException(error);
                });
        });
    }

    _initializeSession() {
        return new Promise((resolve, reject) => {
            const options = {
                method: 'POST',
                body: JSON.stringify({
                    content_type: 20,
                    object_id: this.videoId,
                }),
            };

            fetchRequest('/api/chats/', options)
                .then((data) => {
                    if (!data?.uuid) {
                        reject();
                        Sentry.captureException(new Error('Invalid response from the server'));
                        return;
                    }

                    this.uuid = data.uuid;
                    resolve();
                })
                .catch((error) => {
                    reject();
                    Sentry.captureException(error);
                });
        });
    }

    _renderChatbotElement(questions) {
        const questionsMarkdown = (() => {
            const output = [];

            questions.forEach((question, index) => {
                output.push(
                    `<button type="button" id="${index + 1}" data-type="${question.type}" class="vm-chatbot-placeholder__suggestion js_chatbotPlaceholderSuggestion">
                        ${question.question}
                    </button>`,
                );
            });

            return output.join('');
        })();

        const markup = `<div class="vm-chatbot-video-assistant" data-collapsed="false">
            <div class="vm-chatbot-header">
                <h2><i class="icon icon-star"></i>Ask about this video</h2>
                <button type="button" class="vm-button vm-button--sm vm-button--chatbot-collapse js_chatbotCollapse">
                    <i class="icon icon-chevron-up"></i>
                </button>
            </div>
            <div class="vm-chatbot-body js_chatbotBody">
                <div class="vm-chatbot-body__loader js_chatbotLoader"></div>
                <div class="vm-chatbot-placeholder">
                    <div class="vm-chatbot-placeholder__suggestions js_chatbotPlaceholderSuggestions">
                        ${questionsMarkdown}
                    </div>
                </div>
                <div class="vm-chatbot-messages">
                    <div class="vm-chatbot-skeleton js_vmChatbotSkeleton">
                        <div class="vm-chatbot-skeleton__teaser-message-wrap">
                            <div class="vm-chatbot-skeleton__teaser-message-avatar js_chatbotTeaserMessageAvatar"></div>
                            <div class="vm-chatbot-skeleton__teaser-message-text js_chatbotTeaserMessageText">
                                <span>Summarizing...</span>
                            </div>
                        </div>
                        <div class="vm-chatbot-skeleton__lines-wrap">
                            ${this._skeletonLines()}
                        </div>
                    </div>
                </div>
            </div>
            <div class="vm-chatbot-footer">
                <form class="vm-chatbot-footer__form">
                    <i class="icon icon-chatbot"></i>
                    <input type="text"
                            name="question"
                            class="vm-chatbot-input"
                            autocomplete="off"
                            placeholder="${Copy.placeholder.default}"
                            data-type="user_question"
                            aria-label="Question for AI" />
                    <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-chatbot-send">
                        <i class="icon icon-arrow-up"></i>
                    </button>
                </form>
            </div>
        </div>`;

        const template = document.createElement('template');
        template.innerHTML = markup;

        const containerElement = template.content.firstChild;

        // Set up form listeners for submitting a question
        const form = containerElement.querySelector('form');
        const inputElement = form.querySelector('input[type="text"]');

        inputElement.addEventListener('keydown', (ev) => {
            ev.stopPropagation();
        });

        form.addEventListener('submit', (ev) => {
            ev.preventDefault();

            if (this.inProgress) {
                return;
            }

            const question = inputElement.value.trim();
            inputElement.value = '';

            if (!question) {
                return;
            }

            this._askQuestion(question, inputElement.getAttribute('data-type') || '');
        });

        // Set up listeners for collapsing an assistant
        containerElement.querySelector('.js_chatbotCollapse').addEventListener('click', (ev) => {
            ev.preventDefault();

            const targetIcon = containerElement.querySelector('.vm-button--chatbot-collapse .icon');

            if (containerElement.getAttribute('data-collapsed') === 'true') {
                containerElement.setAttribute('data-collapsed', 'false');
                targetIcon.classList.add('icon-chevron-up');
                targetIcon.classList.remove('icon-chevron-down');
            } else {
                containerElement.setAttribute('data-collapsed', 'true');
                targetIcon.classList.remove('icon-chevron-up');
                targetIcon.classList.add('icon-chevron-down');
            }
        });

        return containerElement;
    }

    _renderQuestion(question) {
        if (!this.chatbotElement) {
            return;
        }

        const questionElement = document.createElement('div');
        const skeletonElement = this.chatbotElement.querySelector('.js_vmChatbotSkeleton');
        questionElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--question');
        questionElement.innerText = question;

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');
        questionElement.prepend(iconElement);

        const messagesElement = this.chatbotElement.querySelector('.vm-chatbot-messages');
        messagesElement.insertBefore(questionElement, skeletonElement);

        const scrollOffset = 15;

        setTimeout(() => {
            messagesElement.scrollTo({
                top: messagesElement.scrollHeight - scrollOffset,
                behavior: 'smooth',
            });
        }, _CSS_COLLAPSE_DURATION);
    }

    _answerPrepareElement() {
        const answerElement = document.createElement('div');
        answerElement.classList.add(
            'vm-chatbot-message',
            'vm-chatbot-message--answer',
            'vm-chatbot-message--answer-incomplete',
        );

        return answerElement;
    }

    _answerPrettifyLinks(links) {
        const prettifyLink = (linkElement) => {
            const linkHref = new URL(linkElement.getAttribute('href') || '');
            const timestamp = parseInt(linkHref.searchParams.get('t') || '', 10);
            const samePath = window.location.pathname === linkHref.pathname;

            const textElement = document.createElement('span');
            textElement.innerText = linkElement.innerText;
            linkElement.innerText = '';
            linkElement.appendChild(textElement);

            const iconElement = document.createElement('i');
            iconElement.classList.add('icon', 'icon-play-circle');
            linkElement.prepend(iconElement);

            if (Number.isInteger(timestamp)) {
                const timestampElement = document.createElement('strong');
                const timestampValue = secondsToTimestamp(timestamp);

                timestampElement.innerText = `[${timestampValue}]`;
                linkElement.appendChild(timestampElement);
            }

            if (samePath) {
                if (Number.isInteger(timestamp)) {
                    textElement.innerText = '';
                }

                linkElement.addEventListener('click', (ev) => {
                    ev.preventDefault();

                    if (Number.isInteger(timestamp)) {
                        this.brightcovePlayer?._player.currentTime(timestamp);
                    }
                });
            } else {
                linkElement.setAttribute('target', '_blank');
            }
        };

        links.forEach((el) => prettifyLink(el));
    }

    _answerRenderContent(answerElement, answer, nextBestQuestions = []) {
        if (!this.chatbotElement) {
            return;
        }

        answerElement.innerHTML = parse(answer);
        this._answerPrettifyLinks(answerElement.querySelectorAll('a'));

        const messagesElement = this.chatbotElement.querySelector('.vm-chatbot-messages');
        const skeletonElement = this.chatbotElement.querySelector('.js_vmChatbotSkeleton');
        const prevScrollHeight = messagesElement.scrollHeight;

        messagesElement.insertBefore(answerElement, skeletonElement);

        if (nextBestQuestions?.length) {
            const nextBestQuestionsElement = this._renderNextBestQuestions(nextBestQuestions);
            messagesElement.insertBefore(nextBestQuestionsElement, skeletonElement);
        }

        // Make sure that user is at the same scroll position to avoid flickering during answer streaming
        const newScrollHeight = messagesElement.scrollHeight;
        const diff = newScrollHeight - prevScrollHeight;
        messagesElement.scrollTop += diff;
    }

    _renderNextBestQuestions(nextBestQuestions) {
        const nbqContainer = document.createElement('div');
        nbqContainer.classList.add('vm-chatbot-message', 'vm-chatbot-message--answer', 'vm-chatbot-message--nbq');

        const nbqHeading = document.createElement('h3');
        nbqHeading.innerText = 'Follow-up questions...';

        nbqContainer.appendChild(nbqHeading);

        nextBestQuestions.forEach((question) => {
            const questionElement = document.createElement('button');
            questionElement.setAttribute('type', 'button');
            questionElement.classList.add('vm-chatbot--nbq');

            const iconElement = document.createElement('i');
            iconElement.classList.add('icon', 'icon-chatbot');

            const textElement = document.createElement('span');
            textElement.innerText = question.question;

            questionElement.appendChild(iconElement);
            questionElement.appendChild(textElement);

            questionElement.addEventListener('click', (ev) => {
                ev.preventDefault();
                this._askQuestion(question.question, question.type);
            });

            nbqContainer.appendChild(questionElement);
        });

        return nbqContainer;
    }

    _render(container, questions = []) {
        this.chatbotElement = this._renderChatbotElement(questions);
        container.appendChild(this.chatbotElement);

        this._placeholderSuggestionsClickHandler();
        this._loadAnimation();
    }

    /**
     * Retrieve predefined questions for the provided content ID.
     *
     * @returns {Promise<{
     *   status: 'error' | 'forbidden' | 'success',
     *   questions: Array<{ question: QuestionObject }>
     * }>}
     */
    _retrievePredefined() {
        return new Promise((resolve) => {
            resolve({
                status: 'success',
                questions: _SUGGESTED_QUESTIONS,
            });
        });
    }

    _placeholderSuggestionsClickHandler() {
        const placeholderSuggestions = Array.from(
            this.chatbotElement.querySelectorAll('.js_chatbotPlaceholderSuggestion'),
        );

        placeholderSuggestions.forEach((suggestionElement) => {
            suggestionElement.addEventListener('click', (ev) => {
                ev.preventDefault();
                this._askQuestion(ev.target.innerText, 'predefined');
            });
        });
    }

    _loadAnimation() {
        const lottieAnimationAnswerPath = document.querySelector('meta[name="lottie-ai-answer"]')?.content;

        if (lottieAnimationAnswerPath) {
            const answerLoadAnimation = lottie.loadAnimation({
                container: this.chatbotElement.querySelector('.js_chatbotTeaserMessageAvatar'),
                renderer: 'svg',
                loop: true,
                autoplay: true,
                path: lottieAnimationAnswerPath,
            });

            answerLoadAnimation.addEventListener('DOMLoaded', () => {
                answerLoadAnimation.setSpeed(0.66);
                answerLoadAnimation.playSegments([45, 164], true);
            });
        }
    }

    _skeletonLines() {
        const lines = [];
        for (let i = 0; i < 12; i++) {
            const index = (i + 1) % 4 > 0 ? (i + 1) % 4 : 4;
            lines.push(`<div class="vm-chatbot-skeleton__line vm-chatbot-skeleton__line--${index}"></div>`);
        }
        return lines.join('');
    }

    _toggleSkeleton(show = true, showText = true) {
        if (show) {
            this.chatbotElement.querySelector('.js_vmChatbotSkeleton').classList.add('is-visible');

            if (showText) {
                this.chatbotElement
                    .querySelector('.vm-chatbot-skeleton__teaser-message-wrap')
                    ?.classList.remove('hidden');

                if (!this.skeletonMessageInterval) {
                    const spans = this.chatbotElement.querySelectorAll('.js_chatbotTeaserMessageText span');
                    let current = 0;

                    // Hide all spans except the first one
                    spans.forEach((span, i) => {
                        span.style.display = i === 0 ? 'inline' : 'none';
                    });

                    this.skeletonMessageInterval = setInterval(() => {
                        // Hide current
                        spans[current].style.display = 'none';

                        // Move to next (loop around)
                        current = (current + 1) % spans.length;

                        // Show next
                        spans[current].style.display = 'block';
                    }, 3000);
                }
            } else {
                this.chatbotElement.querySelector('.vm-chatbot-skeleton__teaser-message-wrap')?.classList.add('hidden');
            }
        } else {
            this.chatbotElement.querySelector('.js_vmChatbotSkeleton').classList.remove('is-visible');

            if (this.skeletonMessageInterval) {
                clearInterval(this.skeletonMessageInterval);
                this.skeletonMessageInterval = null;
            }
        }
    }

    async initialize(container) {
        const { status, questions } = await this._retrievePredefined();

        if (status === 'forbidden' || status === 'error') {
            return;
        }

        try {
            await this._initializeChatbot();
            container.classList.remove('hidden');
            this._render(container, questions);
        } catch {
            return;
        }
    }
}
